import { Component, OnInit } from '@angular/core';
import { mergeMap } from 'rxjs/operators';
import { GraphUtils } from 'src/app/classes/GraphUtils';
import { Utils } from 'src/app/classes/Utils';
import { SearchObject } from 'src/app/services/Rest';
import { BaseComponent } from '../base/base.component';

interface ISearch
{
	created;
}

interface AvgValue
{
	count: number;
	value: number;
	name: string;
}

interface TicketData
{
	store_id: number;
	store_name: string;
	avg_ticket: number;
	day: string;
	order_count: number;
}

@Component({
	selector: 'app-report-ticket-avg',
	templateUrl: './report-ticket-avg.component.html',
	styleUrls: ['./report-ticket-avg.component.css']
})
export class ReportTicketAvgComponent extends BaseComponent implements OnInit
{
	avg_order_graph = GraphUtils.getEmptyLineChart();
	days: string[] = [];
	start: string = '';
	endx: string = '';
	startx: string = '';
	isearch: SearchObject<ISearch> = this.getEmptySearch();

	store_averages: Map<string, number> = new Map();
	day_averages: Map<string, number> = new Map();
	grand_average: number = 0;

	ngOnInit(): void
	{
		this.path = '/report-ticket-avg';

		this.subs.sink = this.getQueryParamObservable().pipe
		(
			mergeMap((response) =>
			{
				this.initSearchDates(response[0]);

				let payload = {
					minutes_offset: new Date().getTimezoneOffset(),
					start: Utils.getUTCMysqlStringFromDate(this.isearch.ge.created),
					end: Utils.getUTCMysqlStringFromDate(this.isearch.le.created),
				};

				return this.rest.getReporte('averageTicket', payload);
			})
		)
		.subscribe(
			(response: TicketData[]) => this.processResponse(response),
			(error) => this.showError(error)
		);
	}

	private initSearchDates(params): void
	{
		this.isearch = this.getSearch(params, ['created'], []);

		if (!this.isearch.ge.created)
		{
			let startOfMonth = new Date();
			startOfMonth.setHours(0, 0, 0, 0);
			startOfMonth.setDate(1);
			this.isearch.ge.created = startOfMonth;
		}

		if (!this.isearch.le.created)
		{
			let endOfMonth = Utils.getEndOfMonth(new Date());
			endOfMonth.setHours(23, 59, 59, 0);
			this.isearch.le.created = endOfMonth;
		}

		this.startx = Utils.getLocalMysqlStringFromDate(this.isearch.ge.created as Date);
		this.endx = Utils.getLocalMysqlStringFromDate(this.isearch.le.created as Date);
	}

	private processResponse(response: TicketData[]): void
	{
		// Calculate weighted averages
		let avgByDay = new Map<string, AvgValue>();
		let avgByStore = new Map<string, AvgValue>();
		let allTotals: AvgValue = { count: 0, value: 0, name: 'Total' };

		console.table(response);

		for (let record of response)
		{
			// Aggregate by day
			let dayAvg = avgByDay.get(record.day);
			if (!dayAvg)
			{
				dayAvg = { count: 0, value: 0, name: record.day };
				avgByDay.set(record.day, dayAvg);
			}
			dayAvg.count += record.order_count;
			dayAvg.value += record.order_count * record.avg_ticket;

			// Aggregate by store
			let storeAvg = avgByStore.get(record.store_name);
			if (!storeAvg)
			{
				storeAvg = { count: 0, value: 0, name: record.store_name };
				avgByStore.set(record.store_name, storeAvg);
			}
			storeAvg.count += record.order_count;
			storeAvg.value += record.order_count * record.avg_ticket;

			// Grand total
			allTotals.count += record.order_count;
			allTotals.value += record.order_count * record.avg_ticket;
		}

		// Build sorted days list
		this.days = Array.from(avgByDay.keys()).sort();

		// Build graph data
		this.avg_order_graph.results = this.buildGraphData(response);

		// Calculate store averages (weighted)
		this.store_averages = new Map();
		for (let [name, avg] of avgByStore)
		{
			this.store_averages.set(name, avg.count > 0 ? avg.value / avg.count : 0);
		}

		// Calculate day averages (weighted)
		this.day_averages = new Map();
		for (let [name, avg] of avgByDay)
		{
			this.day_averages.set(name, avg.count > 0 ? avg.value / avg.count : 0);
		}

		// Calculate grand average (weighted)
		this.grand_average = allTotals.count > 0 ? allTotals.value / allTotals.count : 0;
	}

	private buildGraphData(response: TicketData[]): any[]
	{
		let storeDataMap = new Map<number, TicketData[]>();
		let storeNames = new Map<number, string>();

		// Initialize empty data for each store
		for (let record of response)
		{
			if (!storeDataMap.has(record.store_id))
			{
				storeNames.set(record.store_id, record.store_name);
				storeDataMap.set(record.store_id, this.days.map(day => ({
					day,
					avg_ticket: 0,
					order_count: 0,
					store_name: record.store_name,
					store_id: record.store_id
				})));
			}

			let storeData = storeDataMap.get(record.store_id);
			let dayIndex = this.days.indexOf(record.day);
			if (dayIndex >= 0)
			{
				storeData[dayIndex] = record;
			}
		}

		// Convert to graph format
		let graphData = [];
		for (let [storeId, data] of storeDataMap)
		{
			graphData.push({
				name: storeNames.get(storeId),
				series: data.map(d => ({ name: d.day, value: d.avg_ticket }))
			});
		}

		return graphData;
	}

	searchReport(): void
	{
		this.search(this.isearch);
	}
}
